import { iife, isNone, isSome, Maybe } from '@crocodille/common';
import cn from 'classnames';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useFocusWithin } from '../../hooks/use-focus-within';
import Input from '../elements/input-2/input-2';
import styles from './search-autocomplete.module.scss';
import LocationIcon from '@components/icons/location-icon';

export interface SearchAutocompleteProps<S> {
  /**
   * Must be globally unique ID.
   *
   * TODO(React 18): Replace with `useID`.
   */
  readonly componentId: string;
  /**
   * Label of the input.
   */
  readonly label: string;
  /**
   * Name of the search form input element.
   */
  readonly name?: string;
  /**
   * The text in the search field of the component.
   *
   * This is a controlled input, no changed to the UI will be shown unless
   * values given by `onSearchTextChange` are reflected in this prop.
   *
   * @see onSearchTextChange
   */
  readonly searchText: string;
  /**
   * Called when search text is changed by the user.
   *
   * @param searchText Current search text
   */
  onSearchTextChange(searchText: string): void;
  /**
   * Called when user picks a suggestion.
   *
   * @param suggestion The picked suggestion.
   */
  onSuggestionPicked?(suggestion: S): void;
  /**
   * Given a suggestion, must return a unique string key to identify it.
   *
   * @param suggestion
   */
  createSuggestionKey(suggestion: S): string;
  /**
   * Given a suggestion, must return a string label for presenting it to the user.
   * @param suggestion
   */
  createSuggestionLabel(suggestion: S): string;
  /**
   * For a given suggestion, create a React node to be used as an icon.
   *
   * @param suggestion
   */
  getSuggestionIcon?(suggestion: S): Maybe<React.ReactNode>;
  /**
   * A list of suggestion to present to the user.
   */
  readonly suggestions?: Maybe<readonly S[]>;
  /**
   * A busy indicator, for example: The system is loading new suggestion.
   */
  readonly busy?: boolean;
  /**
   * Class name for container element.
   */
  readonly className?: Maybe<string>;
  /**
   * When changed, suggestion is picked.
   */
  readonly selectSuggestion?: Maybe<S>;

  readonly isInputFocused?: boolean;
}

/**
 * Manages an active item from given list of items.
 * - Activations of next / previous item are wrapping.
 * - The active item is kept active as long as it's in the items list.
 * - The first item is activated if the previously active item is removed.
 *
 * @param items List of items to pick from
 * @param createItemKey Function to create a unique key for an item
 * @param initialActiveItemKey Initialize with this key as active
 *
 * @returns State of the active item
 */
function useActiveItem<I>(
  items: readonly I[],
  createItemKey: (item: I) => string,
  initialActiveItemKey?: Maybe<string>,
) {
  const [activeItemKey, setActiveItemKey] = useState(initialActiveItemKey);

  const activeItem = useMemo(
    () => items.find((item) => activeItemKey === createItemKey(item)),
    [items, activeItemKey, createItemKey],
  );

  const activeItemIndex = useMemo(() => {
    const index = items.findIndex(
      (item) => activeItemKey === createItemKey(item),
    );

    return index === -1 ? null : index;
  }, [items, activeItemKey, createItemKey]);

  useEffect(() => {
    const hasActiveKeyInItems = items.some(
      (item) => createItemKey(item) === activeItemKey,
    );

    if (!hasActiveKeyInItems) {
      setActiveItemKey(items[0] ? createItemKey(items[0]) : null);
    }
  }, [items, activeItemKey, createItemKey]);

  const activateNextItem = () => {
    if (items.length === 0) {
      return;
    }

    const nextIndex = isNone(activeItemIndex)
      ? 0
      : (activeItemIndex + 1) % items.length;

    setActiveItemKey(createItemKey(items[nextIndex]));
  };

  const activatePrevItem = () => {
    if (items.length === 0) {
      return;
    }

    const nextIndex = isNone(activeItemIndex)
      ? 0
      : (activeItemIndex + items.length - 1) % items.length;

    setActiveItemKey(createItemKey(items[nextIndex]));
  };

  return {
    activeItem,
    activeItemKey,
    activateNextItem,
    activatePrevItem,
    setActiveItemKey,
  };
}

/**
 * A combobox with search functionality.
 */
function SearchAutocomplete<S>({
  componentId,
  name,
  label,
  suggestions,
  busy = false,
  createSuggestionKey,
  createSuggestionLabel,
  getSuggestionIcon,
  searchText = '',
  onSearchTextChange,
  onSuggestionPicked,
  className,
  selectSuggestion,
  isInputFocused,
}: SearchAutocompleteProps<S>) {
  //
  // Let's uniquely identify options so we can reference them using aria attributes.
  //
  const createOptionId = useCallback(
    (key: string) => `${componentId}-option-${key}`,
    [componentId],
  );

  //
  // Expanded state for the combobox popup.
  //
  const [expandedState, setExpanded] = useState(false);
  const expanded =
    expandedState && (searchText.length > 0 || (suggestions?.length ?? 0) > 0);

  //
  // Manage active item so we can determine aria-activedescendant.
  //
  const {
    activeItem,
    activeItemKey,
    activateNextItem,
    activatePrevItem,
    setActiveItemKey,
  } = useActiveItem(suggestions ?? [], createSuggestionKey);

  //
  // Ensures that the active descendant is visible when it's changing.
  //
  useEffect(() => {
    if (typeof window !== 'undefined') {
      if (isSome(activeItemKey)) {
        const activeDescendantElement = document.getElementById(
          createOptionId(activeItemKey),
        );

        activeDescendantElement?.scrollIntoView?.({
          behavior: 'auto',
          block: 'nearest',
        });
      }
    }
  }, [activeItemKey, createOptionId, expanded]);

  const pickSuggestion = (suggestion: S) => {
    setActiveItemKey(createSuggestionKey(suggestion));
    onSearchTextChange(createSuggestionLabel(suggestion));
    onSuggestionPicked?.(suggestion);
    setExpanded(false);
  };

  const pickSuggestionRef = useRef(pickSuggestion);
  pickSuggestionRef.current = pickSuggestion;

  useEffect(() => {
    if (isSome(selectSuggestion)) {
      pickSuggestionRef.current(selectSuggestion);
    }
  }, [selectSuggestion]);

  /**
   * We manage the active item while the control is focused.
   *
   * Using down arrow while not expanded, the listbox popup is opened.
   * Using the up and down arrows, the user is able to cycle trough items.
   * Using enter, the item is picked.
   * Using escape the listbox popup is closed.
   */
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown' && !expanded) {
      setExpanded(true);
      e.preventDefault();
    } else if (e.key === 'ArrowDown' && expanded) {
      activateNextItem();
      e.preventDefault();
    } else if (e.key === 'ArrowUp' && expanded) {
      activatePrevItem();
      e.preventDefault();
    } else if (e.key === 'Enter' && expanded && isSome(activeItem)) {
      pickSuggestion(activeItem);
      e.preventDefault();
    } else if (e.key === 'Escape' && expanded) {
      setExpanded(false);
      e.preventDefault();
    }
  };

  const activeDescendantElementId = activeItemKey
    ? createOptionId(activeItemKey)
    : null;

  const listboxId = `${componentId}-suggestions`;

  const containerRef = useRef<HTMLDivElement>(null);
  const focusWithin = useFocusWithin(containerRef);

  useEffect(() => {
    setExpanded(focusWithin);
  }, [focusWithin]);

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    isInputFocused && inputRef.current?.focus();
  }, [isInputFocused]);

  return (
    <div ref={containerRef} className={cn(styles.container, className)}>
      <Input
        id={`${componentId}-input`}
        name={name}
        className={styles.searchInput}
        type="text"
        role="combobox"
        placeholder={label}
        autoComplete="off"
        aria-label={label}
        aria-expanded={expanded}
        aria-controls={listboxId}
        aria-autocomplete="list"
        aria-activedescendant={activeDescendantElementId ?? ''}
        aria-owns={[`${componentId}-suggestions`, activeDescendantElementId]
          .filter(isSome)
          .join(' ')}
        value={searchText}
        onChange={onSearchTextChange}
        onKeyDown={handleKeyDown}
        //
        // The beforeInput event is a newer replacement for keyPress.
        // The point here is to capture only keys corresponding to printable characters.
        //
        onBeforeInput={() => setExpanded(true)}
        onClick={() => setExpanded(true)}
        showClearButton={true}
        ref={inputRef}
      >
        <Input.Icon>
          <div className={styles.locationIcon}>
            <LocationIcon />
          </div>
        </Input.Icon>
      </Input>
      <ul
        id={listboxId}
        role="listbox"
        tabIndex={-1}
        aria-hidden={!expanded}
        aria-busy={busy}
        hidden={!expanded}
        className={cn(styles.popup, {
          [styles.isExpanded]: expanded,
          [styles.isBusy]: busy,
        })}
      >
        {(suggestions ?? []).map((suggestion) => {
          const suggestionKey = createSuggestionKey(suggestion);
          const isSelected = activeItemKey === suggestionKey;

          return (
            <li
              key={suggestionKey}
              role="option"
              aria-selected={isSelected}
              id={createOptionId(suggestionKey)}
              className={cn(styles.option, { [styles.isSelected]: isSelected })}
              onClick={(e) => {
                pickSuggestion(suggestion);
                //
                // It's important to prevent default here since the option is associated
                // with the input via aria attributes and clicks are propagated to it.
                //
                e.preventDefault();
              }}
            >
              <span>{createSuggestionLabel(suggestion)}</span>
              {iife(() => {
                const icon = getSuggestionIcon?.(suggestion);

                if (icon) {
                  return <span className={styles.icon}>{icon}</span>;
                }

                return null;
              })}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default SearchAutocomplete;
